import { AxiosInstance } from "axios";
import { Indicateur } from "../types/Indicateur";
import { Contenu, ContenuResponse } from "../types/Contenu";
import { IndicateurResponse } from "../types/Indicateur";
import { FIELD_BASE_TYPES } from "../types/BaseTypes";

export const indicateurService = {
  loadMultiIdDatas: async (
    api:AxiosInstance,
    indicateurs:Indicateur[]
  ):Promise<Record<number,any>> => {
    if (!api || !indicateurs) throw new Error("Demande invalide");
    const data: Record<number, any> = {};

    for (const ind of indicateurs) {
      const rows = (await indicateurService.calculateIndicateurById(api,ind.id)).data;

      data[ind.id] = {
        meta: {
          id: ind.id,
          nom: ind.nom,
        },
        rows,
      };
    }
    return data;
  },

  validateFormuleNumericFields: (
      formule: string,
      contenus: Contenu[],
      fonctions: string[],
      operations: string[]
    ): string | null => {
    for (const contenu of contenus) {
      if (!contenu.nomContenu) continue;

      // présence du champ dans la formule (mot entier)
      const regex = new RegExp(`\\b${contenu.nomContenu}\\b`, "g");

      if (regex.test(formule)) {
        const baseType = contenu.baseType?.value;

        if (
          !baseType ||
          (baseType !== FIELD_BASE_TYPES.NUMBER.value &&
          baseType !== FIELD_BASE_TYPES.DECIMAL.value)
        ) {
          return `Le champ "${contenu.nomContenu}" n'est pas numérique (${contenu.baseType?.label || "type inconnu"})`;
        }
      }
    }

    let formuleClean = formule;

    for (const contenu of contenus) {
      if (!contenu.nomContenu) continue;
      const escapedName = contenu.nomContenu.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(?<!\\w)${escapedName}(?!\\w)`, "gu");
      formuleClean = formuleClean.replace(regex, "");
    }

    // Supprime les fonctions
    for (const fn of fonctions) {
      const regex = new RegExp(`\\b${fn}\\b`, "g");
      formuleClean = formuleClean.replace(regex, "");
    }

    // Supprime les opérations (en échappant les caractères spéciaux)
    for (const op of operations) {
      const regex = new RegExp(`\\${op}`, "g");
      formuleClean = formuleClean.replace(regex, "");
    }

    // Supprime les espaces
    formuleClean = formuleClean.replace(/\s+/g, "");

    if (formuleClean.length > 0) {
      return `La formule contient des caractères non autorisés: "${formuleClean}"`;
    }

    return null;
  },


  getAllIndicateurs: async (
    api: AxiosInstance
  ): Promise<Indicateur[]> => {
    try {
      const response = await api.get("/indicateur/all");
      return response.data as Indicateur[];
    } catch (error: any) {
      console.error("Erreur lors du chargement des indicateurs :", error);
      throw error.response?.data || error;
    }
  },

  createIndicateur: async (
    api: AxiosInstance,
    indicateur: Indicateur
  ): Promise<IndicateurResponse> => {
    try {
      const response = await api.post(
        "/indicateur/create",
        indicateur
      );
      return response.data as IndicateurResponse;
    } catch (error: any) {
      console.error("Erreur lors de la création de l'indicateur :", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de la création de l'indicateur"
      };
    }
  },

  getContenus: async (
    api: AxiosInstance,
    idFenetre: number,
    idSousFenetre?: number
  ): Promise<ContenuResponse> => {
    try {
      const response = await api.get("/indicateur/contenus", {
        params: {
          idFenetre,
          ...(idSousFenetre ? { idSousFenetre } : {})
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error("Erreur lors du chargement des contenus :", error);

      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors du chargement des contenus"
      };
    }
  },

  getIndicateursByFenetre: async (
    api: AxiosInstance,
    idFenetre: number,
    idSousFenetre?: number
  ): Promise<Indicateur[]> => {
    try {
      const response = await api.get("/indicateur/byFenetre", {
        params: {
          idFenetre,
          ...(idSousFenetre != null ? { idSousFenetre } : {})
        }
      });
      return response.data as Indicateur[];
    } catch (error: any) {
      console.error("Erreur lors du chargement des indicateurs par fenêtre :", error);
      throw error.response?.data || error;
    }
  },

  updateIndicateur: async (
    api: AxiosInstance,
    id: number,
    indicateur: Indicateur
    ): Promise<IndicateurResponse> => {
    try {
        const response = await api.put(
        `/indicateur/${id}`,
        indicateur
        );
        return response.data as IndicateurResponse;
    } catch (error: any) {
        console.error("Erreur lors de la mise à jour :", error);
        return {
        success: false,
        message: error.response?.data?.message || "Erreur lors de la mise à jour"
        };
    }
  },

  accepterIndicateur: async (
    api: AxiosInstance,
    id: number
  ): Promise<IndicateurResponse> => {
    try {
      const response = await api.put(`/indicateur/${id}/accept`);
      return response.data as IndicateurResponse;
    } catch (error: any) {
      console.error("Erreur lors de l’acceptation :", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors de l’acceptation de l’indicateur"
      };
    }
  },

  declinerIndicateur: async (
    api: AxiosInstance,
    id: number
  ): Promise<IndicateurResponse> => {
    try {
      const response = await api.put(`/indicateur/${id}/decline`);
      return response.data as IndicateurResponse;
    } catch (error: any) {
      console.error("Erreur lors du refus :", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors du refus de l’indicateur"
      };
    }
  },
  calculateIndicateurById: async (
  api: AxiosInstance,
  id: number
  ): Promise<{ success: boolean; data?: any[]; message?: string }> => {
    try {
      const response = await api.get(`/indicateur/${id}/calculate`);
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error("Erreur lors du calcul de l'indicateur :", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "Erreur lors du calcul de l'indicateur"
      };
    }
  }
};